import clonedeep from 'lodash.clonedeep';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component, Fragment } from 'react';
import { compose } from 'lodash/fp';
import {
  calculateTotalPricePerCurrency,
  findCurrencyItemFromListWithFallback,
  getInitialCurrencyState,
} from '../../../util/currency';
import { errorCodes } from '../../../util/errorCodes';
import { isNullOrEmpty } from '../../../util/isNullOrEmpty';
import jsonPost from '../../../util/jsonPost';
import Button from '../Atoms/Button';
import {
  accomTypes,
  getOptionUpgrade,
  getOptionUpgradesInGroup,
  getTotalPrice,
  roomTypeCodes,
} from '../BookingFieldset/helpers';
import {
  StopoverTypes,
  StopoverTypesLabel,
  createSelectedStopover,
  getCheapestStopoverData,
  getCheapestStopoverDataPrice,
  getMaxStopoverDataNumberOfNights,
  getMinStopoverDataNumberOfNights,
  getStopoverDataSoldOut,
  getTourDatesWithStopoversConsidered,
} from '../BookingStopoverOptions/helpers';
import { withErrorBoundary } from '../Core/ErrorBoundary';
import ErrorMessage from '../Core/ErrorMessage';
import Modal from '../Core/Modal';
import ItinerarySidebar from '../ItinerarySidebar';
import PackageSummary from '../PackageSummary';
import StickyComponent from '../StickyComponent';
import ButtonToggle from './ButtonToggle';
import {
  addTent,
  buildDefaultCampingSelection,
  getCampsiteFromPrice,
  getCampingAccomOptions,
  getCampsiteOptions,
  getPitchesForCampsite,
  getPitchFromPrice,
  getSelectedCamping,
  getTentPrice,
  getTentsForPitch,
  removeTent,
  selectCampsite,
  selectPitch,
  setTentCount,
  TENT_PRICING_MODE,
} from './campingHelpers';
import { runFeasibility } from './campingFeasibility';
import Excursions from './Excursions';
import Extras from './Extras';
import HotelGroup from './HotelGroup';
import OptionsCarousel from './OptionsCarousel';
import { eventOptionsPackagePropTypes, labelsPropTypes } from './PropTypes';
import RoomSelection from './RoomSelection';
import TicketGroup from './TicketGroup';
import UpgradeDropdown from './UpgradeDropdown';
import {
  buildCapacityErrorMessage,
  buildStopoverItineraryDescriptions,
  formatTitle,
  injectItineraryDescriptions,
  isAtolProtected,
  removeEmptySlots,
  formatPaxValidationErrorMessage,
  PAX_ERRORS,
} from './helpers';
import CUSTOM_EVENT from '../../../util/customEvents';
import withMapReversePortal from '../../../hocs/withMapReversePortal';
import { portalNodeType } from '../../../util/sharedPropTypes';
import GoogleMapComponent from '../GoogleMapComponent';
import PaxSelectorInput from './PaxSelectorInput';
import UpgradeCard from '../BookingFieldset/UpgradeCard';
import { IconSource } from '../Atoms/Icon';

const propTypes = exact({
  apiURL: PropTypes.string.isRequired,
  labels: labelsPropTypes,
  orderId: PropTypes.string.isRequired,
  package: eventOptionsPackagePropTypes.isRequired,
  tourId: PropTypes.string.isRequired,
  selectedStep: PropTypes.string.isRequired,
  // Below is props from withReversePortal HOC
  portalNode: portalNodeType,
});

const defaultProps = {
  labels: undefined,
  portalNode: null,
};

class BookingUpgrades extends Component {
  constructor(props) {
    super(props);

    this.handleExcursionSelect = this.handleExcursionSelect.bind(this);
    this.handleHotelSelect = this.handleHotelSelect.bind(this);
    this.handleItineraryToggleClick = this.handleItineraryToggleClick.bind(this);
    this.handleModalClose = this.handleModalClose.bind(this);
    this.handleModalOpen = this.handleModalOpen.bind(this);
    this.handleMotorhomeSelect = this.handleMotorhomeSelect.bind(this);
    this.handleMotorhomeBerthsChange = this.handleMotorhomeBerthsChange.bind(this);
    this.handlePricePerPaxChange = this.handlePricePerPaxChange.bind(this);
    this.handleRoomChange = this.handleRoomChange.bind(this);
    this.handleStopoverRemove = this.handleStopoverRemove.bind(this);
    this.handleStopoverSelect = this.handleStopoverSelect.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleTicketSelect = this.handleTicketSelect.bind(this);
    this.handleResetState = this.handleResetState.bind(this);
    this.handleAccomTypeChange = this.handleAccomTypeChange.bind(this);
    this.handleCurrencyChange = this.handleCurrencyChange.bind(this);
    this.handleCampsiteSelect = this.handleCampsiteSelect.bind(this);
    this.handlePitchSelect = this.handlePitchSelect.bind(this);
    this.handleTentSelect = this.handleTentSelect.bind(this);
    this.handleTentCountChange = this.handleTentCountChange.bind(this);
    this.handleTentRemove = this.handleTentRemove.bind(this);

    const currentCurrency = getInitialCurrencyState(
      props.package?.pricePerPax[0]?.pricePerCurrency
    );
    const totalPricePerCurrency = calculateTotalPricePerCurrency(props.package);
    const currencyPriceSetting = findCurrencyItemFromListWithFallback(
      totalPricePerCurrency,
      currentCurrency.code
    );

    const state = {
      ...clonedeep(props.package?.selectedOptions),
      activeModalId: null,
      selectedBerthSizes: this.getSuitableBerthSizes(),
      callToBook: false,
      errorMessage: null,
      isLoading: false,
      itinerary: props.package?.itinerarySideBar?.itinerary,
      itineraryUpdated: false,
      currentCurrencyCodeAndSymbol: currentCurrency,
      currencyPriceSetting,
      hasUserInteracted: false,
    };

    state.accommodationType = this.getDefaultAccomType(state);

    state.totalPrice = getTotalPrice(state, props.package, currencyPriceSetting);

    // Calculate total party size
    const totalPartySize = Object.values(state.paxSelection).reduce((sum, count) => sum + count, 0);

    // Update party size related state
    state.partySize = totalPartySize;

    this.state = state;
  }

  componentDidMount() {
    window.addEventListener('pageshow', this.handleResetState);
    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.handleResetState);
    window.removeEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  handleCurrencyChange(event) {
    const newCurrencyCode = event?.detail?.code;
    if (newCurrencyCode) {
      this.setState({ currentCurrencyCodeAndSymbol: event.detail });
    } else console.error('Currency data not found for:', newCurrencyCode);
    const newCurrencyPriceSetting = findCurrencyItemFromListWithFallback(
      this.props.package.pricePerPax[0].pricePerCurrency,
      newCurrencyCode
    );

    if (newCurrencyPriceSetting) {
      const existingState = this.state;
      this.setState({
        currencyPriceSetting: newCurrencyPriceSetting,
        totalPrice: getTotalPrice(existingState, this.props.package, newCurrencyPriceSetting),
      });
    } else {
      console.error('Currency not found from current currency chosen to currency from model');
    }
  }

  handleResetState() {
    this.setState({ isLoading: false });
  }

  getAccomOptions = (accomType) =>
    accomType && accomType !== accomTypes.MULTI
      ? this.props.package?.accommodationOptions?.filter((ao) => ao.accommodationType === accomType)
      : this.props.package?.accommodationOptions;

  getSelectedAccoms = (accomType) =>
    accomType && accomType !== accomTypes.MULTI
      ? this.state.accommodations?.filter((a) => a.accommodationType === accomType) || []
      : this.state.accommodations || [];

  getDefaultAccomType = (state) => {
    const { package: data } = this.props;
    let accommodationType = state.accommodationType;

    if (data.enforceSingleAccommodationType) {
      if (data.selectedOptions.accommodationType === accomTypes.MULTI) {
        const firstAvailableType = (data.accommodationOptions || [])[0]?.accommodationType;
        accommodationType = firstAvailableType ?? accomTypes.HOTEL;
      }
    } else if (data.accommodationOptions.length) {
      // if only one accom type is present, set accommodationType to that type
      if (
        data.accommodationOptions.every((accom) => accom.accommodationType === accomTypes.HOTEL)
      ) {
        accommodationType = accomTypes.HOTEL;
      } else if (
        data.accommodationOptions.every((accom) => accom.accommodationType === accomTypes.MOTORHOME)
      ) {
        accommodationType = accomTypes.MOTORHOME;
      } else if (
        data.accommodationOptions.every((accom) => accom.accommodationType === accomTypes.CAMPING)
      ) {
        accommodationType = accomTypes.CAMPING;
      }
    }

    return accommodationType;
  };

  getSuitableBerthSizes(partySize) {
    const defaultPartySize = partySize || this.props.package?.selectedOptions?.partySize || 1;
    const sizes = [];
    this.getAccomOptions(accomTypes.MOTORHOME).forEach((mo) => {
      const options = this.getBerthOptionsForMotorhomeGroup(mo.key, defaultPartySize) || [];
      const values = options.map((o) => o.value).sort();
      const bestFit = values.find((v) => v >= defaultPartySize) || 0;
      sizes.push({ groupKey: mo.key, bestFit });
    });
    return sizes;
  }

  parseStopoverOptions({ key, options, stopoverType }) {
    const selectedKey =
      this.state.stopovers.find((stopover) => stopover.groupKey === key)?.optionKey || '';

    return options.map((stopover) => {
      const price = getCheapestStopoverDataPrice(stopover.data, this.state.currencyPriceSetting);
      return {
        description: stopover.option.fullDescription,
        isSelected: stopover.option.key === selectedKey,
        key: stopover.key,
        modalProps: {
          images: stopover.option.images,
          title: stopover.option.heroBannerTitleText,
          subTitle: stopover.option.heroBannerSubtitleText,
          desc: stopover.option.heroBannerDescription,
          content: stopover.option.modalContent,
          price,
          priceFromText: this.props.labels.overlayPriceFromLabelText,
          durationMin: getMinStopoverDataNumberOfNights(stopover.data),
          durationMax: getMaxStopoverDataNumberOfNights(stopover.data),
        },
        price,
        singleImageArray: [!stopover.option.images?.length || stopover.option.images[0]],
        soldOut: getStopoverDataSoldOut(stopover.data),
        soldOutText: this.props.labels.soldOutText,
        subtitle: StopoverTypesLabel[stopoverType] || '',
        title: stopover.option.title,
      };
    });
  }

  parseMotorhomeOptions({ key, defaultOption = {}, upgrades = [] }, selectedBerthSize = 0) {
    const allOptions = [defaultOption, ...upgrades];
    const selectedKey =
      this.getSelectedAccoms(accomTypes.MOTORHOME).find((sa) => sa.groupKey === key)?.optionKey ||
      '';
    return allOptions
      .filter((motorhome) => motorhome.option.numberOfBerth >= selectedBerthSize)
      .map((motorhome) => {
        const price = findCurrencyItemFromListWithFallback(
          motorhome.totalPricePerCurrency,
          this.state.currencyPriceSetting.currency
        )?.price;
        return {
          description: motorhome.option.fullDescription,
          isSelected: motorhome.key === selectedKey,
          key: motorhome.key,
          limitedAvailability: motorhome.limitedAvailability,
          limitedAvailabilityText: motorhome.limitedAvailabilityText,
          modalProps: {
            images: motorhome.option.images,
            title: motorhome.option.heroBannerTitleText,
            subTitle: motorhome.option.heroBannerSubtitleText,
            desc: motorhome.option.heroBannerDescription,
            content: motorhome.option.modalContent,
            fromPriceText: this.props.labels.overlayPriceFromLabelText,
            price,
            motorhomeBerthText: this.props.labels?.motorhomeOverlayBerthLabelText,
            motorhomeGearboxText: this.props.labels?.motorhomeOverlayGearboxLabelText,
            motorhomeType: motorhome.option.motorhomeType,
            motorhomeTypeText: this.props.labels?.motorhomeOverlayTypeLabelText,
            numberOfBerth: motorhome.option.numberOfBerth,
            gearbox: motorhome.option.gearbox,
          },
          price,
          singleImageArray: [!motorhome.option.images?.length || motorhome.option.images[0]],
          soldOut: motorhome.soldOut,
          soldOutText: this.props.labels.soldOutText,
          subtitle:
            this.props.labels.MotorhomeBerthsText?.replace(
              '{numberOfBerths}',
              motorhome.option.numberOfBerth
            ) ?? `${motorhome.option.numberOfBerth} Berth`,
          title: motorhome.option.title,
        };
      });
  }

  getBerthOptionsForMotorhomeGroup(groupKey, partySize) {
    const berths = [];
    const allMotorhomes = getOptionUpgradesInGroup(
      groupKey,
      this.getAccomOptions(accomTypes.MOTORHOME)
    );
    allMotorhomes.forEach((m) => {
      if (!m.option?.numberOfBerth || berths.includes(m.option?.numberOfBerth)) return;
      berths.push(m.option?.numberOfBerth);
    });
    berths.sort();
    return berths.filter((b) => b >= partySize).map((b) => ({ text: b, value: b }));
  }

  handlePricePerPaxChange(paxTypeKey, paxCount) {
    const mutableState = clonedeep(this.state);

    // Initialize paxSelection if not exists
    mutableState.paxSelection = mutableState.paxSelection || {};
    mutableState.paxSelection[paxTypeKey] = paxCount;

    // Calculate total party size
    const totalPartySize = Object.values(mutableState.paxSelection).reduce(
      (sum, count) => sum + count,
      0
    );

    // Update party size related state
    mutableState.partySize = totalPartySize;
    mutableState.callToBook = totalPartySize > this.props.package.maxPartySize;
    mutableState.hasUserInteracted = true; // Always set to true when pax changes

    // Update berth sizes based on party size
    mutableState.selectedBerthSizes = this.getSuitableBerthSizes(totalPartySize);
    mutableState.selectedBerthSizes.forEach((berthSize) => {
      if (!berthSize.bestFit) {
        mutableState.accommodations = mutableState.accommodations.filter(
          (accom) =>
            accom.accommodationType === accomTypes.MOTORHOME &&
            accom.groupKey !== berthSize.groupKey
        );
        return;
      }
      const optionUpgrades = getOptionUpgradesInGroup(
        berthSize.groupKey,
        this.getAccomOptions(accomTypes.MOTORHOME)
      );
      const firstSuitableMotorhome = optionUpgrades.find(
        (ou) => !ou.soldOut && ou.option.numberOfBerth >= berthSize.bestFit
      );
      const selectedMotorhome = mutableState.accommodations.find(
        (accom) =>
          accom.accommodationType === accomTypes.MOTORHOME && accom.groupKey === berthSize.groupKey
      );
      if (selectedMotorhome) {
        selectedMotorhome.optionKey = firstSuitableMotorhome.option.key;
      } else {
        mutableState.accommodations.push({
          accommodationType: accomTypes.MOTORHOME,
          groupKey: berthSize.groupKey,
          optionKey: firstSuitableMotorhome.option.key,
          rooms: null,
        });
      }
    });

    // Update total price
    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );

    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  handleMotorhomeBerthsChange(groupKey, selectedBerthSize) {
    const mutableState = clonedeep(this.state);
    const optionUpgrades = getOptionUpgradesInGroup(
      groupKey,
      this.getAccomOptions(accomTypes.MOTORHOME)
    );
    const firstSuitableMotorhome = optionUpgrades.find(
      (ou) => !ou.soldOut && ou.option.numberOfBerth >= selectedBerthSize
    );
    mutableState.accommodations.find(
      (a) => a.accommodationType === accomTypes.MOTORHOME && a.groupKey === groupKey
    ).optionKey = firstSuitableMotorhome?.option.key;
    mutableState.selectedBerthSizes.find((b) => b.groupKey === groupKey).bestFit =
      selectedBerthSize;
    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  handleRoomChange(roomCount, roomKey) {
    const { rooms = [] } = this.getSelectedAccoms(accomTypes.HOTEL)?.[0] || {};
    const mutableState = clonedeep(this.state);
    // Selected room state may be null within hotel state,
    // to access selected room state we use the first hotel,
    // as all room state is replicated across all hotels.
    const room = rooms.find((r) => r.roomKey === roomKey);
    if (room) {
      if (room.roomCount === roomCount) return;
      room.roomCount = roomCount;
    } else {
      rooms.push({ roomCount, roomKey });
    }
    // the selected room state is mirrored across all selected hotels,
    // as the same room config once set is the same for all.
    mutableState.accommodations = mutableState.accommodations.map((accom) =>
      accom.accommodationType === accomTypes.HOTEL
        ? {
            ...accom,
            rooms,
          }
        : accom
    );
    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  handleHotelSelect(groupKey, optionKey) {
    const selectedHotels = this.getSelectedAccoms(accomTypes.HOTEL);
    if (selectedHotels.find((a) => a.groupKey === groupKey && a.optionKey === optionKey)) return;
    const mutableState = clonedeep(this.state);

    const accomOptions = this.getAccomOptions(accomTypes.HOTEL);
    // Get selected hotel
    const foundHotel = getOptionUpgrade(groupKey, optionKey, accomOptions);

    // Reset room state
    const roomTypes = foundHotel.roomTypes || [];
    const rooms = roomTypes.map((roomType) => ({
      roomKey: roomType.key,
      roomCount: 0,
    }));
    mutableState.accommodations = mutableState.accommodations.map((accom) =>
      accom.accommodationType === accomTypes.HOTEL
        ? {
            ...accom,
            rooms,
          }
        : accom
    );
    mutableState.accommodations.find(
      (a) => a.accommodationType === accomTypes.HOTEL && a.groupKey === groupKey
    ).optionKey = optionKey;
    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  handleTicketSelect(groupKey, optionKey) {
    const mutableState = clonedeep(this.state);
    if (mutableState.tickets.find((t) => t.groupKey === groupKey && t.optionKey === optionKey))
      return;
    mutableState.tickets.find((t) => t.groupKey === groupKey).optionKey = optionKey;
    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  handleMotorhomeSelect(groupKey, optionKey) {
    const mutableState = clonedeep(this.state);
    const motorhomeGroup = mutableState.accommodations.find(
      (a) => a.accommodationType === accomTypes.MOTORHOME && a.groupKey === groupKey
    );
    if (motorhomeGroup?.optionKey === optionKey) return;
    if (motorhomeGroup) {
      motorhomeGroup.optionKey = optionKey;
    } else {
      mutableState.accommodations.push({
        accommodationType: accomTypes.MOTORHOME,
        groupKey,
        optionKey,
        rooms: null,
      });
    }

    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  // TODO group indexes are used in legacy logic to determine group, we can get empty slots, refactor when there's time
  handleStopoverSelect(groupKey, optionKey) {
    const mutableState = clonedeep(this.state);
    const selectedStopover = mutableState.stopovers.find(
      (stopover) => stopover.groupKey === groupKey
    );
    const selectedStopoverIndex = mutableState.stopovers.indexOf(selectedStopover);
    if (selectedStopover?.optionKey === optionKey) return;

    const { options, stopoverType } =
      this.props.package?.stopoverOptions.find(({ key }) => key === groupKey) || {};
    if (!options) return;

    const { data, option } = options.find((opt) => opt.option.key === optionKey);
    const cheapestStopoverData = getCheapestStopoverData(data, this.state.currencyPriceSetting);

    // Store stopover key and new price
    const stopoverData = {
      dataKey: cheapestStopoverData?.key || '',
      excursion: null,
      flights: cheapestStopoverData?.flightOptions?.length
        ? cheapestStopoverData?.flightOptions.map((flightOption) => ({
            flightClassKey: flightOption.defaultOption.flightClasses?.[0].key,
            groupKey: flightOption.key,
            optionKey: flightOption.defaultOption.key,
          }))
        : null,
      groupKey,
      hotel: option.hotelOption
        ? {
            accommodationType: accomTypes.HOTEL,
            groupKey: option.hotelOption.key,
            optionKey: option.hotelOption.defaultOption.key,
            rooms: option.hotelOption.defaultOption.roomTypes.map((roomType) => ({
              roomKey: roomType.key,
              roomCount: 0,
            })),
          }
        : null,
      optionKey,
      stopoverType: Object.values(StopoverTypes).indexOf(stopoverType),
    };

    if (selectedStopover) {
      mutableState.stopovers.splice(selectedStopoverIndex, 1, stopoverData);
    } else {
      createSelectedStopover(mutableState, stopoverData);
    }

    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  handleStopoverRemove(groupKey) {
    const mutableState = clonedeep(this.state);
    const groupIndex = mutableState.stopovers.findIndex((s) => s.groupKey === groupKey);
    mutableState.stopovers.splice(groupIndex, 1);
    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  handleExcursionSelect(groupKey, optionKey) {
    const mutableState = clonedeep(this.state);
    if (mutableState.excursions.find((e) => e.groupKey === groupKey && e.optionKey === optionKey)) {
      mutableState.excursions = mutableState.excursions.filter((e) => e.groupKey !== groupKey);
    } else if (mutableState.excursions.find((e) => e.groupKey === groupKey)) {
      mutableState.excursions.find((e) => e.groupKey === groupKey).optionKey = optionKey;
    } else {
      mutableState.excursions = [...mutableState.excursions, { groupKey, optionKey }];
    }
    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  handleItineraryToggleClick() {
    this.setState({ itineraryUpdated: false });
  }

  handleModalOpen(activeModalId) {
    document.body.classList.add('active-modal');
    this.setState({ activeModalId });
  }

  handleModalClose() {
    document.body.classList.remove('active-modal');
    this.setState({ activeModalId: null });
  }

  handleAccomTypeChange(value) {
    const mutableState = clonedeep(this.state);
    if (!this.getSelectedAccoms(value).length) {
      const accomOptions = this.getAccomOptions(value);
      accomOptions.forEach((accomOption) => {
        if (value === accomTypes.CAMPING) {
          const defaultCamping = buildDefaultCampingSelection(accomOption);
          if (defaultCamping) mutableState.accommodations.push(defaultCamping);
          return;
        }
        mutableState.accommodations.push({
          accommodationType: value,
          groupKey: accomOption.key,
          optionKey: accomOption.defaultOption.option.key,
          rooms:
            value === accomTypes.HOTEL
              ? accomOption.defaultOption.roomTypes.map((roomType) => ({
                  roomKey: roomType.key,
                  roomCount: 0,
                }))
              : null,
        });
      });
    }
    mutableState.accommodationType = value;
    this.setState(mutableState);
  }

  mutateCampingSelection(groupKey, mutator) {
    const mutableState = clonedeep(this.state);
    const accommodations = mutableState.accommodations || [];
    const hasExisting = accommodations.some(
      (accom) => accom.accommodationType === accomTypes.CAMPING && accom.groupKey === groupKey
    );

    if (hasExisting) {
      mutableState.accommodations = accommodations.map((accom) =>
        accom.accommodationType === accomTypes.CAMPING && accom.groupKey === groupKey
          ? mutator(accom)
          : accom
      );
    } else {
      const campingGroup = getCampingAccomOptions(this.props.package).find(
        (g) => g.key === groupKey
      );
      const defaultSelection = buildDefaultCampingSelection(campingGroup);
      if (defaultSelection) {
        mutableState.accommodations = [...accommodations, mutator(defaultSelection)];
      }
    }

    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    mutableState.hasUserInteracted = true;
    this.setState(mutableState);
  }

  handleCampsiteSelect(groupKey, optionKey) {
    const campingGroup = getCampingAccomOptions(this.props.package).find((g) => g.key === groupKey);
    if (!campingGroup) return;
    const current = getSelectedCamping(this.state);
    if (current?.optionKey === optionKey) return;
    this.mutateCampingSelection(groupKey, (selection) =>
      selectCampsite(selection, campingGroup, optionKey)
    );
  }

  handlePitchSelect(groupKey, pitchKey) {
    const current = getSelectedCamping(this.state);
    if (current?.pitchKey === pitchKey) return;
    this.mutateCampingSelection(groupKey, (selection) => selectPitch(selection, pitchKey));
  }

  handleTentSelect(groupKey, tentKey) {
    // Currently only one tent type may be selected at a time — selecting a new tent clears
    // any previously selected tents before adding the new one.
    // Retained for future use: multi-tent selection via addTent(selection, tentKey)
    this.mutateCampingSelection(groupKey, (selection) =>
      addTent({ ...selection, rooms: [] }, tentKey)
    );
  }

  handleTentCountChange(groupKey, tentKey, count) {
    // If the tent being changed is not already selected and count > 0, clear all rooms first
    // so only one tent type is selected at a time.
    // Retained for future use: multi-tent selection via setTentCount(selection, tentKey, count)
    this.mutateCampingSelection(groupKey, (selection) => {
      const alreadySelected = (selection.rooms || []).some((r) => r.roomKey === tentKey);
      const base = alreadySelected || count <= 0 ? selection : { ...selection, rooms: [] };
      return setTentCount(base, tentKey, count);
    });
  }

  handleTentRemove(groupKey, tentKey) {
    this.mutateCampingSelection(groupKey, (selection) => removeTent(selection, tentKey));
  }

  validateHotelCapacity(selectedHotels, hotelOptions, partySize) {
    if (!selectedHotels?.[0]?.rooms?.length) return null;

    const capacity = selectedHotels[0].rooms.reduce((accumulator, room) => {
      const { roomTypes } =
        getOptionUpgrade(selectedHotels[0].groupKey, selectedHotels[0].optionKey, hotelOptions) ||
        {};
      const { code } = roomTypes.find((r) => r.key === room.roomKey) || {};
      let roomCapacity;
      switch (code) {
        case roomTypeCodes.SINGLE_ROOM:
          roomCapacity = 1;
          break;
        case roomTypeCodes.TRIPLE_ROOM:
          roomCapacity = 3;
          break;
        default:
          roomCapacity = 2;
      }
      return accumulator + room.roomCount * roomCapacity;
    }, 0);

    return buildCapacityErrorMessage(
      capacity,
      partySize,
      this.props.package?.enforceAllRoomsFilledValidation,
      this.props.labels
    );
  }

  validateMotorhome(selectedMotorhomes, motorhomeOptions, partySize) {
    if (!motorhomeOptions.length) return null;

    // Validate motorhome selection
    const isValid = motorhomeOptions.every(({ key }) =>
      selectedMotorhomes.find((selectedMotorhome) => key === selectedMotorhome?.groupKey)
    );
    if (!isValid) return this.props.labels.validationSelectMotorhome;

    // Validate berth size
    const hasBerthLessThanPartySize = selectedMotorhomes.some(
      (m) =>
        getOptionUpgrade(m.groupKey, m.optionKey, motorhomeOptions).option.numberOfBerth < partySize
    );
    if (hasBerthLessThanPartySize) return this.props.labels.validationSelectMotorhomeWithMoreBerths;

    return null;
  }

  validateCamping(selectedCamping, campingOptions, paxSelection) {
    if (!campingOptions.length) return null;

    if (!selectedCamping?.pitchKey || !selectedCamping?.rooms?.length) {
      return this.props.labels.validationSelectCamping;
    }

    const maximumTents = this.props.package?.maximumTentsPerBooking;
    if (maximumTents != null) {
      const totalTents = selectedCamping.rooms.reduce((sum, r) => sum + (r.roomCount || 0), 0);
      if (totalTents > maximumTents) {
        return this.props.labels.maximumTentsPerBookingReachedValidationMessage;
      }
    }

    const campingGroup = campingOptions.find((g) => g.key === selectedCamping.groupKey);
    if (!campingGroup) return null;

    const tents = getTentsForPitch(
      campingGroup,
      selectedCamping.optionKey,
      selectedCamping.pitchKey
    );
    const selectedTents = selectedCamping.rooms
      .map(({ roomKey, roomCount }) => ({
        tent: tents.find((t) => t.option?.key === roomKey),
        count: roomCount,
      }))
      .filter(({ tent }) => Boolean(tent));

    const { isValid, reason } = runFeasibility(selectedTents, paxSelection);
    if (!isValid) {
      if (reason === 'NO_TENTS_SELECTED')
        return this.props.labels.tentSelectionMandatoryValidationMessage;
      if (reason === 'INSUFFICIENT_CAPACITY')
        return this.props.labels.validationSelectCampingWithMoreBerths;
      if (reason === 'PAX_COMBINATION_NOT_SUPPORTED')
        return this.props.labels.tentOccupancyRulesInvalidValidationMessage;
      return this.props.labels.validationSelectCamping;
    }

    return null;
  }

  handleSubmit() {
    const {
      excursions,
      partySize,
      paxSelection,
      stopovers,
      tickets,
      accommodationType,
      currentCurrencyCodeAndSymbol,
    } = this.state;
    const { orderId, tourId } = this.props;

    this.setState({
      errorMessage: null,
      isLoading: true,
    });

    if (this.isApplicableToAccomType(accomTypes.HOTEL)) {
      const hotelOptions = this.getAccomOptions(accomTypes.HOTEL);
      const selectedHotels = this.getSelectedAccoms(accomTypes.HOTEL);
      const capacityErrorMessage = this.validateHotelCapacity(
        selectedHotels,
        hotelOptions,
        partySize
      );

      if (capacityErrorMessage)
        return this.setState({ errorMessage: capacityErrorMessage, isLoading: false });
    }

    if (this.isApplicableToAccomType(accomTypes.MOTORHOME)) {
      const motorhomeOptions = this.getAccomOptions(accomTypes.MOTORHOME);
      const selectedMotorhomes = this.getSelectedAccoms(accomTypes.MOTORHOME);
      const motorhomeErrorMessage = this.validateMotorhome(
        selectedMotorhomes,
        motorhomeOptions,
        partySize
      );
      if (motorhomeErrorMessage)
        return this.setState({ errorMessage: motorhomeErrorMessage, isLoading: false });
    }

    if (this.isApplicableToAccomType(accomTypes.CAMPING)) {
      const campingOptions = this.getAccomOptions(accomTypes.CAMPING);
      const selectedCamping = getSelectedCamping(this.state);
      const campingErrorMessage = this.validateCamping(
        selectedCamping,
        campingOptions,
        paxSelection
      );
      if (campingErrorMessage)
        return this.setState({ errorMessage: campingErrorMessage, isLoading: false });
    }

    // Post form data to API
    const options = {
      accommodations: this.getSelectedAccoms(accommodationType),
      accommodationType,
      excursions,
      orderId,
      paxSelection,
      stopovers: removeEmptySlots(stopovers), // TODO group indexes are used in legacy logic to determine group, we can get empty slots, refactor when there's time
      tickets,
      tourId,
      currency: currentCurrencyCodeAndSymbol.code,
    };

    return jsonPost(this.props.apiURL, options, (response) => {
      if (response.errorCode) {
        if (response.errorCode === errorCodes.TourBookingTourHasReachedPaxCapLimit) {
          window.location.href = response.redirectUri;
        }

        // Set isCallToBook to true if error code is 171
        if (response.errorCode === PAX_ERRORS.PAX_TYPE_NOT_ALLOWED) {
          this.setState({ isCallToBook: true });
        }

        const errorMessage = formatPaxValidationErrorMessage({
          errorCode: response.errorCode,
          labels: this.props.labels,
          paxTypes: this.props.package.paxTypes,
          paxSelection,
        });

        return this.setState({
          errorMessage,
          isLoading: false,
        });
      }
      window.location.href = response.redirectUri;
      return this.setState({ errorMessage: null });
    });
  }

  isApplicableToAccomType(accomType) {
    return (
      (this.props.package?.enforceSingleAccommodationType &&
        this.state.accommodationType === accomType) ||
      !this.props.package?.enforceSingleAccommodationType
    );
  }

  render() {
    const { excursionOptions, extras, stopoverOptions } = this.props.package;
    const { labels } = this.props;

    let { ticketOptions } = this.props.package || {};
    if (!this.props.package?.allowIndividualTicketUpgrade) {
      ticketOptions = [ticketOptions[0]];
    }

    let availableRooms = [];
    if (this.getSelectedAccoms(accomTypes.HOTEL)?.length > 0) {
      const { groupKey, optionKey, rooms } = this.getSelectedAccoms(accomTypes.HOTEL)[0];
      const { roomTypes } = getOptionUpgrade(
        groupKey,
        optionKey,
        this.getAccomOptions(accomTypes.HOTEL)
      );

      this.roomTypes = roomTypes || [];
      availableRooms =
        rooms ||
        roomTypes.map((roomType) => ({
          roomKey: roomType.key,
          roomCount: 0,
        }));
    }

    const itinerarySideBar = {
      ...this.props.package?.itinerarySideBar,
      isCallToBook: this.state.callToBook,
    };
    const { callToBookButtonText } = this.props.package?.itinerarySideBar || {};

    const showAtolLabel = isAtolProtected(this.state.flights);

    const { updatedTourFromDate, updatedTourToDate } = getTourDatesWithStopoversConsidered(
      this.props.package,
      this.state.stopovers
    );

    return (
      <section className="Booking">
        <form noValidate autoComplete="false" onSubmit={this.handleSubmit}>
          {/* Itinerary */}
          <ItinerarySidebar
            {...itinerarySideBar}
            fromDate={updatedTourFromDate}
            toDate={updatedTourToDate}
            handleToggleClick={this.handleItineraryToggleClick}
            hideBookButton
            isUpdated={this.state.itineraryUpdated}
            itinerary={injectItineraryDescriptions(
              this.state,
              this.props.package,
              this.props.selectedStep
            )}
            priceTotal={this.state.totalPrice}
            preEventStopoverItinerary={buildStopoverItineraryDescriptions(
              this.props.package.stopoverOptions,
              this.state.stopovers,
              StopoverTypes.PRE_EVENT
            )}
            postEventStopoverItinerary={buildStopoverItineraryDescriptions(
              this.props.package.stopoverOptions,
              this.state.stopovers,
              StopoverTypes.POST_EVENT
            )}
            perPersonText={labels.perPersonText}
          />
          {/* Package summary */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              <StickyComponent contained>
                <PackageSummary
                  atolProtectedText={labels.atolProtectedText}
                  callToBookText={callToBookButtonText}
                  currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                  durationText={labels.durationText}
                  fromDate={updatedTourFromDate}
                  toDate={updatedTourToDate}
                  description={this.props.package?.tourDescription}
                  isCallToBook={this.state.callToBook}
                  preferredCardImage={this.props.labels.preferredCardImage}
                  price={this.state.totalPrice}
                  showAtolLabel={showAtolLabel}
                  title={this.props.package?.tourName}
                  totalPriceText={labels.totalPriceText}
                />
              </StickyComponent>
            </div>
          </div>
          {/* Party size */}
          <div className="Booking_group">
            <PaxSelectorInput
              handleChange={this.handlePricePerPaxChange}
              paxTypes={this.props.package.paxTypes}
              paxRules={this.props.package.paxRules}
              selectedValues={this.state.paxSelection}
              setErrorMessage={(message) => this.setState({ errorMessage: message })}
              labels={this.props.labels}
              setPaxSelection={(paxSelection) => this.setState({ paxSelection })}
              setIsCallToBook={(isCallToBook) => this.setState({ callToBook: isCallToBook })}
            />
          </div>
          {this.props.package?.enforceSingleAccommodationType &&
            [accomTypes.HOTEL, accomTypes.MOTORHOME, accomTypes.CAMPING].filter(
              (type) => this.getAccomOptions(type)?.length > 0
            ).length > 1 && (
              <div className="Booking_group">
                <h2 className="Booking_groupTitle">
                  {labels.enforceSingleAccommodationTitleText ??
                    'What type of accommodation do you want to stay in?'}
                </h2>
                <div className="Booking_groupDescription">
                  {labels.enforceSingleAccommodationDescriptionText ??
                    'This accommodation type will be for the entirety of your trip'}
                </div>
                <div className="Booking_content Booking_content-inset">
                  <ButtonToggle
                    options={[
                      {
                        text: labels.enforceSingleAccommodationHotelToggleText ?? 'Hotel',
                        value: accomTypes.HOTEL,
                      },
                      {
                        text: labels.enforceSingleAccommodationMotorhomeToggleText ?? 'Motorhome',
                        value: accomTypes.MOTORHOME,
                      },
                      {
                        text: labels.enforceSingleAccommodationCampingToggleText ?? 'Camping',
                        value: accomTypes.CAMPING,
                      },
                    ].filter((option) => this.getAccomOptions(option.value)?.length > 0)}
                    activeOption={this.state.accommodationType}
                    handleChange={this.handleAccomTypeChange}
                  />
                </div>
              </div>
            )}
          {/* Motorhomes */}
          {this.isApplicableToAccomType(accomTypes.MOTORHOME) &&
            this.getAccomOptions(accomTypes.MOTORHOME).map((tourMotorhomeOptionGroup) => {
              const availableMotorhomeOptions = this.getBerthOptionsForMotorhomeGroup(
                tourMotorhomeOptionGroup.key,
                this.state.partySize
              );
              if (!availableMotorhomeOptions.length) {
                return (
                  <div key={tourMotorhomeOptionGroup.key} className="Booking_group">
                    <ErrorMessage text={labels.validationNoMotorhomesAvailableForParty} />
                  </div>
                );
              }
              return (
                <React.Fragment key={tourMotorhomeOptionGroup.key}>
                  <div className="Booking_group">
                    {labels?.selectBerthTitleText && (
                      <h2 className="Booking_groupTitle">{labels?.selectBerthTitleText}</h2>
                    )}
                    <div className="Booking_groupDescription">
                      {labels?.selectBerthDescriptionText}
                    </div>
                    <div className="Booking_content Booking_content-inset">
                      <UpgradeDropdown
                        handleChange={({ target }) =>
                          this.handleMotorhomeBerthsChange(
                            tourMotorhomeOptionGroup.key,
                            target.value
                          )
                        }
                        labelText={labels?.selectBerthLabelText}
                        options={availableMotorhomeOptions}
                        selectedValue={
                          this.state.selectedBerthSizes.find(
                            (b) => b.groupKey === tourMotorhomeOptionGroup.key
                          )?.bestFit
                        }
                      />
                    </div>
                  </div>
                  <div className="Booking_group">
                    {tourMotorhomeOptionGroup?.subTitle && (
                      <h2 className="Booking_groupTitle">{`${tourMotorhomeOptionGroup.subTitle}`}</h2>
                    )}
                    <OptionsCarousel
                      closeModalText={labels.closeModalText}
                      currencySymbol={this.state.currentCurrencyCodeAndSymbol?.symbol}
                      groupKey={tourMotorhomeOptionGroup.key}
                      handleFieldSelect={this.handleMotorhomeSelect}
                      options={this.parseMotorhomeOptions(
                        tourMotorhomeOptionGroup,
                        this.state.selectedBerthSizes.find(
                          (b) => b.groupKey === tourMotorhomeOptionGroup.key
                        )?.bestFit
                      )}
                      perPersonText={labels.perPersonText}
                      viewDetailsCtaText={labels?.motorhomeViewDetailsButtonText}
                    />
                  </div>
                </React.Fragment>
              );
            })}
          {/* Hotels */}
          {this.isApplicableToAccomType(accomTypes.HOTEL) &&
            this.getAccomOptions(accomTypes.HOTEL).map((hotelOption) => {
              const hotelTitle = labels.accommodationTitle
                ? formatTitle(labels.accommodationTitle, hotelOption.subTitle)
                : '';
              return (
                <div className="Booking_group" key={hotelOption.key}>
                  {hotelTitle && <h2 className="Booking_groupTitle">{hotelTitle}</h2>}
                  <HotelGroup
                    closeModalText={labels.closeModalText}
                    currencyPriceSetting={this.state.currencyPriceSetting}
                    currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                    groupKey={hotelOption.key}
                    handleOptionSelect={this.handleHotelSelect}
                    optionAddedText={labels.addedToPackageText}
                    options={[hotelOption.defaultOption, ...hotelOption.upgrades]}
                    perPersonText={labels.perPersonText}
                    priceFromText={labels.overlayPriceFromText}
                    selectedKey={
                      this.getSelectedAccoms(accomTypes.HOTEL)?.find(
                        (a) => a.groupKey === hotelOption.key
                      )?.optionKey || ''
                    }
                    soldOutText={labels.soldOutText}
                    viewDetailsCtaText={labels.hotelViewDetailsButtonText}
                    portalNode={this.props.portalNode}
                  />
                </div>
              );
            })}
          {/* Rooms */}
          {this.isApplicableToAccomType(accomTypes.HOTEL) &&
            availableRooms.length > 0 &&
            labels.roomsTitle && (
              <div className="Booking_group">
                <div className="Booking_content Booking_content-inset">
                  <h2 className="Booking_groupTitle">{labels.roomsTitle}</h2>
                  <div className="Booking_groupDescription">
                    {labels.singleRoomSupplementText ??
                      'If you require a room for one person, a single room supplement (SRS) will apply.'}
                  </div>
                  <RoomSelection
                    handleChange={this.handleRoomChange}
                    partySize={this.state.partySize}
                    roomTypes={this.roomTypes}
                    selectedValues={availableRooms}
                  />
                </div>
              </div>
            )}
          {/* Camping */}
          {this.isApplicableToAccomType(accomTypes.CAMPING) &&
            getCampingAccomOptions(this.props.package).map((campingGroup) => {
              const selectedCamping = getSelectedCamping(this.state);
              const isSelectedGroup = selectedCamping?.groupKey === campingGroup.key;
              const selectedOptionKey = isSelectedGroup ? selectedCamping?.optionKey : '';
              const selectedPitchKey = isSelectedGroup ? selectedCamping?.pitchKey : '';
              const selectedRooms = (isSelectedGroup && selectedCamping?.rooms) || [];
              const campsites = getCampsiteOptions(campingGroup);
              const availableCampsites = campsites.filter((c) => !c.soldOut);
              if (!availableCampsites.length) {
                return (
                  <div key={campingGroup.key} className="Booking_group">
                    <ErrorMessage text={labels.validationNoCampingAvailableForParty} />
                  </div>
                );
              }
              const pitches = selectedOptionKey
                ? getPitchesForCampsite(campingGroup, selectedOptionKey)
                : [];
              const tents = selectedPitchKey
                ? getTentsForPitch(campingGroup, selectedOptionKey, selectedPitchKey)
                : [];
              const { code: currency, symbol } = this.state.currentCurrencyCodeAndSymbol;
              const tentPricingMode =
                this.props.package?.tentPricingMode ?? TENT_PRICING_MODE.PER_UNIT;
              return (
                <Fragment key={campingGroup.key}>
                  <div className="Booking_group">
                    <h2 className="Booking_groupTitle">
                      {labels.selectCampsiteText ?? 'Select Your Campsite'}
                    </h2>
                    <div className="Booking_content">
                      <div className="UpgradeGroup">
                        <div className="UpgradeGroup_itemGroup">
                          {campsites.map((campsite) => {
                            const fromPrice = getCampsiteFromPrice(
                              campsite,
                              currency,
                              tentPricingMode,
                              this.state.paxSelection,
                              this.props.package?.paxTypes
                            );
                            const modalProps = {
                              images: campsite.option.images,
                              title: campsite.option.heroBannerTitleText,
                              subTitle: campsite.option.heroBannerSubtitleText,
                              desc: campsite.option.heroBannerDescription,
                              content: campsite.option.modalContent,
                              price: fromPrice,
                              priceFromText: labels.priceFromText,
                              portalNode: this.props.portalNode,
                            };
                            return (
                              <div
                                className="UpgradeGroup_item UpgradeGroup_item-fullWidth"
                                key={campsite.option?.key}
                              >
                                <UpgradeCard
                                  blockName="UpgradeGroup_card"
                                  closeModalText={labels.closeModalText}
                                  currencySymbol={symbol}
                                  description={campsite.option.fullDescription}
                                  handleClick={() =>
                                    this.handleCampsiteSelect(
                                      campingGroup.key,
                                      campsite.option?.key
                                    )
                                  }
                                  images={campsite.option.images}
                                  isSelected={campsite.option.key === selectedOptionKey}
                                  limitedAvailability={campsite.limitedAvailability}
                                  limitedAvailabilityText={campsite.limitedAvailabilityText}
                                  limitedAvailabilityRemainingSpaces={
                                    campsite.limitedAvailabilityRemainingSpaces
                                  }
                                  limitedAvailabilityRemainingText={
                                    campsite.limitedAvailabilityRemainingText
                                  }
                                  modalProps={modalProps}
                                  perPersonText={labels.perPersonText}
                                  price={fromPrice}
                                  pricePerPerson={tentPricingMode === TENT_PRICING_MODE.PER_PERSON}
                                  rating={campsite.option.rating}
                                  selectedText={labels.optionAddedText}
                                  soldOut={!!campsite.soldOut}
                                  soldOutText={labels.soldOutText}
                                  title={campsite.option.title || 'campsite.option.title missing'}
                                  viewDetailsCtaText={labels.viewDetailsCtaText}
                                  icons={campsite.option.amenities.map((amenity) => ({
                                    type: amenity.type,
                                    value: amenity.value,
                                  }))}
                                  iconsTitle="Facilities"
                                  isFromPrice
                                  fromPriceText={labels.fromPriceText}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  {selectedOptionKey && pitches.length > 0 && (
                    <div className="Booking_group">
                      <h2 className="Booking_groupTitle">
                        {labels.selectPitchText ?? 'Select Your Pitch'}
                      </h2>
                      <div className="Booking_content">
                        <div className="UpgradeGroup">
                          <div className="UpgradeGroup_itemGroup">
                            {pitches.map((pitch) => {
                              const fromPrice = getPitchFromPrice(
                                pitch,
                                currency,
                                tentPricingMode,
                                this.state.paxSelection,
                                this.props.package?.paxTypes
                              );
                              const modalProps = {
                                images: pitch.option.images,
                                title: pitch.option.heroBannerTitleText,
                                subTitle: pitch.option.heroBannerSubtitleText,
                                desc: pitch.option.heroBannerDescription,
                                content: pitch.option.modalContent,
                                price: fromPrice,
                                priceFromText: labels.priceFromText,
                                portalNode: this.props.portalNode,
                              };
                              return (
                                <div className="UpgradeGroup_item" key={pitch.option?.key}>
                                  <UpgradeCard
                                    blockName="UpgradeGroup_card"
                                    closeModalText={labels.closeModalText}
                                    currencySymbol={symbol}
                                    description={pitch.option.fullDescription}
                                    handleClick={() =>
                                      this.handlePitchSelect(campingGroup.key, pitch.option?.key)
                                    }
                                    images={pitch.option.images}
                                    isSelected={pitch.option.key === selectedPitchKey}
                                    limitedAvailability={pitch.limitedAvailability}
                                    limitedAvailabilityText={pitch.limitedAvailabilityText}
                                    limitedAvailabilityRemainingSpaces={
                                      pitch.limitedAvailabilityRemainingSpaces
                                    }
                                    limitedAvailabilityRemainingText={
                                      pitch.limitedAvailabilityRemainingText
                                    }
                                    modalProps={modalProps}
                                    perPersonText={labels.perPersonText}
                                    price={fromPrice}
                                    pricePerPerson={
                                      tentPricingMode === TENT_PRICING_MODE.PER_PERSON
                                    }
                                    rating={pitch.option.rating}
                                    selectedText={labels.optionAddedText}
                                    soldOut={!!pitch.soldOut}
                                    soldOutText={labels.soldOutText}
                                    title={pitch.option.title || 'pitch.option.title missing'}
                                    viewDetailsCtaText={labels.viewDetailsCtaText}
                                    icons={pitch.option.amenities.map((amenity) => ({
                                      type: amenity.type,
                                      value: amenity.value,
                                    }))}
                                    iconsTitle="Facilities"
                                    forceVerticalLayout
                                    isFromPrice
                                    fromPriceText={labels.fromPriceText}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedPitchKey && tents.length > 0 && (
                    <div className="Booking_group">
                      <h2 className="Booking_groupTitle">
                        {labels.selectTentText ?? 'Select Your Tent'}
                      </h2>
                      <div className="Booking_content">
                        <div className="UpgradeGroup">
                          <div className="UpgradeGroup_itemGroup">
                            {tents.map((tent) => {
                              const tentKey = tent.option?.key;
                              const selectedRoom = selectedRooms.find((r) => r.roomKey === tentKey);
                              const isSelected = (selectedRoom?.roomCount ?? 0) > 0;
                              const price = getTentPrice(
                                tent,
                                currency,
                                tentPricingMode,
                                this.state.paxSelection,
                                this.props.package?.paxTypes
                              );
                              const modalProps = {
                                images: tent.option.images,
                                title: tent.option.heroBannerTitleText,
                                subTitle: tent.option.heroBannerSubtitleText,
                                desc: tent.option.heroBannerDescription,
                                content: tent.option.modalContent,
                                price,
                                priceFromText: labels.priceFromText,
                                portalNode: this.props.portalNode,
                              };
                              return (
                                <div className="UpgradeGroup_item" key={tentKey}>
                                  <UpgradeCard
                                    blockName="UpgradeGroup_card"
                                    closeModalText={labels.closeModalText}
                                    currencySymbol={symbol}
                                    description={tent.option.fullDescription}
                                    handleClick={() =>
                                      this.handleTentSelect(campingGroup.key, tent.option?.key)
                                    }
                                    images={tent.option.images}
                                    isSelected={isSelected}
                                    limitedAvailability={tent.limitedAvailability}
                                    limitedAvailabilityText={tent.limitedAvailabilityText}
                                    limitedAvailabilityRemainingSpaces={
                                      tent.limitedAvailabilityRemainingSpaces
                                    }
                                    limitedAvailabilityRemainingText={
                                      tent.limitedAvailabilityRemainingText
                                    }
                                    modalProps={modalProps}
                                    perPersonText={labels.perPersonText}
                                    pricePerPerson={
                                      tentPricingMode === TENT_PRICING_MODE.PER_PERSON
                                    }
                                    rating={tent.option.rating}
                                    selectedText={labels.optionAddedText}
                                    soldOut={!!tent.soldOut}
                                    soldOutText={labels.soldOutText}
                                    title={tent.option.title || 'tent.option.title missing'}
                                    viewDetailsCtaText={labels.viewDetailsCtaText}
                                    useRemoveButton
                                    handleRemoveClick={() =>
                                      this.handleTentRemove(campingGroup.key, tentKey)
                                    }
                                    handleDropdownChange={(event) =>
                                      this.handleTentCountChange(
                                        campingGroup.key,
                                        tentKey,
                                        Number(event.target.value)
                                      )
                                    }
                                    dropdownLabel={labels.tentQaunityLabelText ?? 'How many tents'}
                                    dropdownOptions={Array.from(
                                      {
                                        length:
                                          (this.props.package?.maximumTentsPerBooking ?? 10) + 1,
                                      },
                                      (_, i) => i
                                    ).map((n) => ({
                                      text: n.toString(),
                                      value: n,
                                    }))}
                                    selectedDropdownKey={selectedRoom?.roomCount ?? 0}
                                    forceVerticalLayout
                                    price={price}
                                    strapLine={
                                      labels.tentCapacityText?.replace(
                                        '{NumberOfBerths}',
                                        tent.option.numberOfBerths
                                      ) ?? `${tent.option.numberOfBerths} Persons`
                                    }
                                    icons={tent.option.amenities.map((amenity) => ({
                                      type: amenity.type,
                                      value: amenity.value,
                                    }))}
                                    iconsTitle={labels.campingAmenitiesText ?? 'Facilities'}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Fragment>
              );
            })}
          {/* Tickets */}
          {ticketOptions.map((ticketGroup) => {
            const ticketTitle = formatTitle(labels.ticketTitle, ticketGroup.subTitle);
            return (
              <div className="Booking_group" key={ticketGroup.key}>
                {ticketTitle && <h2 className="Booking_groupTitle">{ticketTitle}</h2>}
                <TicketGroup
                  groupKey={ticketGroup.key}
                  handleFieldSelect={this.handleTicketSelect}
                  options={[ticketGroup.defaultOption, ...ticketGroup.upgrades]}
                  selectedKey={
                    this.state.tickets.find((t) => t.groupKey === ticketGroup.key)?.optionKey || ''
                  }
                  selectedText={labels.addedToPackageText}
                  soldOutText={labels.soldOutText}
                  currencyPriceSetting={this.state.currencyPriceSetting}
                  currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                  perPersonText={labels.perPersonText}
                />
                {ticketGroup.stadium && (
                  <div className="Booking_footer">
                    <Button
                      blockName="Booking_footerButton"
                      buttonType="button"
                      text={labels.stadiumButtonText}
                      handleClick={() => this.handleModalOpen(ticketGroup.stadium.key)}
                    />
                    <Modal
                      key={ticketGroup.stadium.key}
                      handleModalClose={this.handleModalClose}
                      modalIsOpen={ticketGroup.stadium.key === this.state.activeModalId}
                      title={ticketGroup.stadium.title}
                      carouselImages={ticketGroup.stadium.images}
                    >
                      <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: ticketGroup.stadium.fullDescription }}
                      />
                    </Modal>
                  </div>
                )}
              </div>
            );
          })}
          {/* Excursions (previous Extras or ExtraGroup) */}
          {excursionOptions?.map((excursionOptionGroup) => (
            <div className="Booking_group" key={excursionOptionGroup.key}>
              {!isNullOrEmpty(excursionOptionGroup.subTitle) && (
                <h2 className="Booking_groupTitle">{excursionOptionGroup.subTitle}</h2>
              )}
              {isNullOrEmpty(excursionOptionGroup.subTitle) &&
                !isNullOrEmpty(excursionOptionGroup.day) && (
                  <h2 className="Booking_groupTitle">
                    {labels.excursionsTitle} Day {excursionOptionGroup.day}
                  </h2>
                )}
              {isNullOrEmpty(excursionOptionGroup.subTitle) &&
                isNullOrEmpty(excursionOptionGroup.day) && (
                  <h2 className="Booking_groupTitle">{labels.excursionsTitle}</h2>
                )}
              <Excursions
                currencyPriceSetting={this.state.currencyPriceSetting}
                currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                dateText={labels.excursionsDateText}
                groupKey={excursionOptionGroup.key}
                handleFieldSelect={this.handleExcursionSelect}
                options={excursionOptionGroup.options}
                perPersonText={labels.perPersonText}
                selectedKey={
                  this.state.excursions.find((e) => e.groupKey === excursionOptionGroup.key)
                    ?.optionKey || ''
                }
                selectedText={labels.addedToPackageText}
                timeText={labels.excursionsTimeText}
              />
            </div>
          ))}
          {/* Extras (previously Misc Extras) */}
          <div className="Booking_group">
            {extras.length > 0 && labels.extrasTitle && (
              <>
                <h2 className="Booking_groupTitle">{labels.extrasTitle}</h2>
                <Extras options={extras} perPersonText={labels.perPersonText} />
              </>
            )}
          </div>
          {/* Stopovers */}
          {stopoverOptions.length > 0 && (
            <>
              {stopoverOptions.map((tourStopoverOptionGroup) => (
                <div className="Booking_group" key={tourStopoverOptionGroup.key}>
                  {tourStopoverOptionGroup.subTitle && (
                    <h2 className="Booking_groupTitle">{`${tourStopoverOptionGroup.subTitle}`}</h2>
                  )}
                  <OptionsCarousel
                    closeModalText={labels.closeModalText}
                    currencyPriceSetting={this.state.currencyPriceSetting}
                    currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                    groupKey={tourStopoverOptionGroup.key}
                    handleFieldRemove={this.handleStopoverRemove}
                    handleFieldSelect={this.handleStopoverSelect}
                    options={this.parseStopoverOptions(tourStopoverOptionGroup)}
                    perPersonText={labels.perPersonText}
                    removeFromPackageText={labels.removeFromPackageText}
                    useRemoveButton={this.props.package.stopoverOptionsOptional}
                    viewDetailsCtaText={labels.stopoverViewDetailsButtonText}
                    portalNode={this.props.portalNode}
                  />
                </div>
              ))}
            </>
          )}
          {/* Submit */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              {this.state.errorMessage && (
                <div className="Booking_item">
                  <ErrorMessage text={this.state.errorMessage} />
                </div>
              )}
              <div className="Booking_item Booking_item-center">
                {!this.state.callToBook && (
                  <Button
                    buttonType="button"
                    handleClick={this.handleSubmit}
                    isLoading={this.state.isLoading}
                    modifier="fill"
                    text={labels.submitButtonText}
                    isDisabled={!this.state.hasUserInteracted}
                  />
                )}
                {this.state.callToBook && (
                  <div className="Booking_callToBook">{callToBookButtonText}</div>
                )}
              </div>
            </div>
          </div>
        </form>
      </section>
    );
  }
}

BookingUpgrades.propTypes = propTypes;
BookingUpgrades.defaultProps = defaultProps;
// We render Map inside a portal and BookingStopoverOptions is the component we want to wrap.
// This reverse portal allows us to use 1 map instance across a variety of modals on BookingStopoverOptions
// without mount/unmount and memory leaks.
// The HOC will provide portalNode to BookingUpgrades, and render Map inside Portals.InPortal.
export default compose(
  withErrorBoundary,
  withMapReversePortal(GoogleMapComponent, {
    skip: (props) => !props.labels.googleApiKey,
  })
)(BookingUpgrades);
